import React from 'react';
import Header from 'awesome-pdf-viewer/dist/layout/Header';
import LeftPanel from 'awesome-pdf-viewer/dist/layout/LeftPanel';
import Main from 'awesome-pdf-viewer/dist/layout/Main';
import ViewPanel from 'awesome-pdf-viewer/dist/layout/ViewPanel';
import Toolbar from 'awesome-pdf-viewer/dist/toolbar/Toolbar';
import Pagination from 'awesome-pdf-viewer/dist/toolbar/Pagination';
import Zoom from 'awesome-pdf-viewer/dist/toolbar/Zoom';
import { PdfSource, SearchResult, ViewerMode } from 'awesome-pdf-viewer/dist/viewer/ViewerBase';
import PdfViewer from 'awesome-pdf-viewer';
import PreviewLeftPanel from './left-panel';
import { IFileGroup } from './left-panel/left-panel-types';
import UTEButton from '../ute-button';
import './pdf-preview.style.scss';
import { DownloadIcon } from 'src/assets/svg/download-icon';
import '../../import';
import UTEClientViewWrapper from '../ute-client-view-wrapper';
import UTEDocumentSearchBox from '../ute-document-search-box';
import SearchIcon from '../../assets/svg/search-icon';
import { DeviceType } from 'src/hooks/useDeviceType';

type PdfPreviewProps = {
	fileGroups: IFileGroup[];
	pdfSource: PdfSource;
	onFileSelect: (url: string, data: any) => void;
	onFileDownload: () => void;
	fileExtension?: string;
	tooltip?: string;
	hideLeftPanel?: boolean;
	searchText?: string;
	onCancel?: () => void;
	deviceType?: DeviceType;
};

type PdfPreviewState = {
	textToFind: string;
	showClearButton: boolean;
	searchResult: SearchResult;
	searchIndex: number;
	searchTextChanged: boolean;
	lastPdfSourceUrl: string;
	isStateChanged: boolean;
	isPageRendering: boolean;
};

const initialSearchResult: SearchResult = {
	matchCount: 0,
	pagesMatch: [],
};

export class PdfPreview extends React.Component<PdfPreviewProps, PdfPreviewState> {
	private _viewPanel: any;
	private _toolbar: any;
	constructor(props: any) {
		super(props);
		this.state = {
			textToFind: '',
			showClearButton: true,
			searchResult: initialSearchResult,
			searchIndex: 0,
			lastPdfSourceUrl: '',
			searchTextChanged: false,
			isStateChanged: false,
			isPageRendering: false,
		};
	}

	UNSAFE_componentWillReceiveProps() {
		if (this.props.searchText && this.props.searchText.length > 0 && !this.state.searchTextChanged) {
			this.setState({ textToFind: this.props.searchText });
		}
	}

	componentDidMount() {
		this.setReferences();
	}

	setReferences() {
		this._toolbar && this._toolbar.setViewerReference(this._viewPanel);
		this._viewPanel && this._viewPanel.setToolbarReference(this._toolbar);
	}

	componentDidUpdate() {
		this.setReferences();
	}

	onChangeSearchText = (newSearchText: string) => {
		const disallowedChars = /[\\/:*?"<>|]/g;
		const sanitizedValue = newSearchText.replace(disallowedChars, '');
		this.setState({ textToFind: sanitizedValue, searchTextChanged: true, showClearButton: true });
		if (newSearchText.length == 0) {
			this.onClearSearch();
		}
	};

	getSearchTextResult = (searchResult: SearchResult) => {
		if (
			searchResult.matchCount !== this.state.searchResult.matchCount ||
			searchResult.pagesMatch.length !== this.state.searchResult.pagesMatch.length
		) {
			this.setState({ searchResult: searchResult, isStateChanged: true });
		}
	};

	onSearch = () => {
		const _self = this;
		this._viewPanel.resetSearchIndex();
		this.setState({ searchResult: initialSearchResult }, () => {
			this._viewPanel.searchText(this.state.textToFind).then((searchResult: SearchResult) => {
				_self.setState({ searchResult: searchResult, isStateChanged: true });
			});
		});
	};

	onClearSearch = () => {
		this.setState(
			{
				textToFind: '',
				searchResult: initialSearchResult,
				searchIndex: 0,
				showClearButton: false,
				isStateChanged: true,
				searchTextChanged: true,
			},
			() => {
				this._viewPanel.resetSearchIndex();
				this._viewPanel.resetSearchResult();
			},
		);
	};

	handleNext = () => {
		this._viewPanel.handleNext();
	};

	handlePrevious = () => {
		this._viewPanel.handlePrev();
	};

	getCurrentSearchInex = (index: number) => {
		this.setState({ searchIndex: index + 1, isStateChanged: true });
	};

	getPageRenderingStatus = (isLoading: boolean) => {
		this.setState({ isPageRendering: isLoading });
	};

	hasLessHeaderSpaceForDownloadBtn = () => {
		const width = window.innerWidth;
		return width <= 900;
	};

	public render() {
		return (
			<>
				<PdfViewer id={'awesome-pdf-viewer'}>
					<Header>
						<Toolbar
							ref={(ref: any) => (this._toolbar = ref)}
							hideRightPanel={true}
							isChildComponentUpdated={this.state.searchTextChanged || this.state.isStateChanged}
							onViewModeChange={(mode: ViewerMode) => {}}>
							{this.props.fileExtension && this.props.fileExtension.toLowerCase() !== '.pdf' ? <></> : <Pagination />}
							{this.props.fileExtension && this.props.fileExtension.toLowerCase() !== '.pdf' ? <></> : <Zoom />}
							<UTEClientViewWrapper dataTestAuto='19d3e87e-8d97-4530-a126-c630694e2298'>
								{this.props.searchText !== undefined && this.props.searchText.length === 0 ? (
									<UTEButton
										className='download-btn'
										dataTestAuto='F48A59BC-957B-4DB5-8E8B-08071C2A207F'
										disabled={false}
										text={this.hasLessHeaderSpaceForDownloadBtn() ? '' : 'Download'}
										title={this.props.tooltip ?? 'Download your tax documents.'}
										icon={<DownloadIcon />}
										onClick={() => {
											this.props.onFileDownload();
										}}
									/>
								) : (
									<UTEDocumentSearchBox
										searchText={this.state.textToFind}
										placeholderText='Search'
										searchIcon={<SearchIcon />}
										disabled={this.state.textToFind.length < 3}
										searchOnClick={true}
										showClearButton={this.state.showClearButton}
										totalWidth='269px'
										height='31px'
										maxLength={260}
										onChangeSearchText={this.onChangeSearchText}
										onSearch={this.onSearch}
										onClearSearch={this.onClearSearch}
										navigateToNextSearch={this.handleNext}
										navigateToPreviousSearch={this.handlePrevious}
										totalMatchCount={this.state.searchResult.matchCount}
										searchIndex={this.state.searchIndex}
									/>
								)}
							</UTEClientViewWrapper>
						</Toolbar>
					</Header>
					<Main>
						{!this.props.hideLeftPanel && (
							<LeftPanel>
								<PreviewLeftPanel
									onFileSelect={this.props.onFileSelect}
									fileGroups={this.props.fileGroups}></PreviewLeftPanel>
							</LeftPanel>
						)}
						{this.props.fileExtension && this.props.fileExtension.toLowerCase() !== '.pdf' ? (
							<>
								<div className='page-viewer'>
									<div className='non-supported-file-container'>
										Unable to view the file? No problem! Click the button above to download the file and open it on your
										device.
									</div>
								</div>
							</>
						) : (
							<ViewPanel
								ref={(ref: any) => (this._viewPanel = ref)}
								pdfSource={this.props.pdfSource}
								pdfWorkerSource={`${process.env.PUBLIC_URL}/pdf.worker.min.mjs`}
								searchText={this.state.textToFind.replace(/[-[\]{}()*+?.,\\^$|#]/g, '\\$&')}
								getSearchResultCallBack={this.getSearchTextResult}
								trackSearchIndex={true}
								getCurrentSearchIndexCallBack={this.getCurrentSearchInex}
								isNavigateToFirstSearchMatch={true}
								trackPageRendering={true}
								getPageRenderingStatusCallBack={this.getPageRenderingStatus}></ViewPanel>
						)}
					</Main>
				</PdfViewer>
				{this.props.searchText !== undefined && this.props.searchText.length > 0 && (
					<div className='pdf-preview-modal-footer'>
						<div>
							<UTEButton
								className='download-btn'
								dataTestAuto='194E0EEE-E378-487F-9FDD-694F2AA7C81B'
								disabled={false}
								text='Download'
								title={this.props.tooltip ?? 'Download your tax documents.'}
								icon={<DownloadIcon />}
								onClick={() => {
									this.props.onFileDownload();
								}}
							/>
						</div>

						<div className='modal-actions'>
							<UTEButton
								className='cancel-btn'
								dataTestAuto='FBE09282-5637-421E-90F3-B17A325073FD'
								disabled={false}
								text='Cancel'
								title='Cancel'
								onClick={() => {
									this.props.onCancel && this.props.onCancel();
								}}
							/>

							<UTEButton
								className='confirm-btn'
								dataTestAuto='18FB2D7A-2575-47F2-BB7B-75FB54B3A4AE'
								disabled={false}
								text='OK'
								title='OK'
								onClick={() => {
									this.props.onCancel && this.props.onCancel();
								}}
							/>
						</div>
					</div>
				)}
			</>
		);
	}
}

export default PdfPreview;
